/**
 * @file Composable for smoothly animating values using requestAnimationFrame-based interpolation.
 * Provides a reusable hook for creating smooth transitions between changing values with configurable easing.
 */

import type { Ref } from 'vue'
import { onUnmounted, ref, watch } from 'vue'

/**
 * Animation constants for smooth value interpolation
 */
const DEFAULT_SNAP_THRESHOLD = 0.01 // Snap to target when difference is below this threshold (prevents infinite animation)
const DEFAULT_EASING_FACTOR = 0.15 // Easing factor for smooth interpolation (0.15 = smooth, 0.3 = faster, 0.05 = slower)

/**
 * Linear interpolation (lerp) function for smooth easing between values
 * Formula: result = start + (end - start) * factor
 *
 * @param start - Starting value
 * @param end - Target value
 * @param factor - Interpolation factor (0 = start, 1 = end, 0.15 = smooth easing)
 * @returns Interpolated value between start and end
 */
function lerp(start: number, end: number, factor: number): number {
  return start + (end - start) * factor
}

interface UseSmoothAnimationOptions {
  /**
   * Snap to target when difference is below this threshold
   * @default 0.01
   */
  snapThreshold?: number

  /**
   * Easing factor for smooth interpolation (lower = smoother, higher = faster)
   * @default 0.15
   */
  easingFactor?: number

  /**
   * Whether to start animation immediately
   * @default true
   */
  immediate?: boolean
}

/**
 * Composable for smoothly animating a value toward a target using requestAnimationFrame
 *
 * This hook creates a smoothly interpolated value that follows a target value with easing.
 * It uses requestAnimationFrame for smooth 60fps animation and automatically cleans up
 * on component unmount to prevent memory leaks.
 *
 * @example
 * ```ts
 * const targetAngle = computed(() => calculateAngle(mouseX.value, mouseY.value))
 * const smoothAngle = useSmoothAnimation(targetAngle)
 * // smoothAngle.value will smoothly follow targetAngle.value with easing
 * ```
 *
 * @param target - Reactive target value to animate toward (ref or computed)
 * @param options - Configuration options for animation behavior
 * @returns Ref containing the smoothly animated value
 */
function useSmoothAnimation(target: Ref<number>, options: UseSmoothAnimationOptions = {}): Ref<number> {
  const { snapThreshold = DEFAULT_SNAP_THRESHOLD, easingFactor = DEFAULT_EASING_FACTOR, immediate = true } = options

  // The smoothly interpolated value that will be returned
  const smoothValue = ref(target.value)

  // Animation frame ID for cleanup
  let animationFrameId: number | null = null

  /**
   * Recursive animation loop that smoothly interpolates smoothValue toward target
   * - Uses lerp for smooth easing instead of linear progression
   * - Snaps to target when close enough to prevent infinite animation
   * - Automatically stops when target is reached
   */
  const animate = (targetValue: number) => {
    const current = smoothValue.value
    const diff = Math.abs(targetValue - current)

    // Snap to target when difference is negligible (prevents infinite micro-adjustments)
    if (diff < snapThreshold) {
      smoothValue.value = targetValue
      animationFrameId = null
      return
    }

    // Smoothly interpolate toward target using easing factor
    smoothValue.value = lerp(current, targetValue, easingFactor)

    // Continue animation loop
    animationFrameId = requestAnimationFrame(() => animate(targetValue))
  }

  // Watch target value and start animation when it changes
  watch(
    target,
    (newTarget) => {
      // Cancel any existing animation to prevent multiple concurrent loops
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }

      // Start animation loop
      animationFrameId = requestAnimationFrame(() => animate(newTarget))
    },
    { immediate },
  )

  // Cleanup animation on component unmount to prevent memory leaks
  onUnmounted(() => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }
  })

  return smoothValue
}
export { useSmoothAnimation }
export type { UseSmoothAnimationOptions }
